import { useEffect, type RefObject } from 'react';

/**
 * Calls `handler` when a click lands outside the element referenced by `ref`.
 * Automatically no-ops when `enabled` is false (avoids unnecessary listeners).
 *
 * When `boundaryRef` is provided the handler only fires for clicks that are
 * **outside `ref`** but **inside `boundaryRef`**.  This is useful when a
 * collapsible lives inside a larger container (e.g. the softphone) and clicks
 * outside the container should be ignored.
 *
 * Ignores clicks on targets that have been removed from the DOM
 * (e.g. an item that unmounted before the click event bubbled).
 */
export function useClickOutside<
  T extends HTMLElement = HTMLElement,
  B extends HTMLElement = HTMLElement,
>(
  ref: RefObject<T | null>,
  handler: () => void,
  enabled = true,
  boundaryRef?: RefObject<B | null>
): void {
  useEffect(() => {
    if (!enabled) return;

    const onClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!target.isConnected) return;

      // Click is inside the element itself — ignore.
      if (ref.current?.contains(target)) return;

      // If a boundary was provided, only act when the click is inside it.
      if (boundaryRef && !boundaryRef.current?.contains(target)) return;

      handler();
    };

    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, [ref, handler, enabled, boundaryRef]);
}
